import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslationApp } from "../../lib/i18next";
import { useDispatchApp, useSelectorApp } from "../../lib/redux";
import {
  startGetAgencyLinkingPrompt,
  startLinkAdAccountsToAgency,
  startListenerAgencyLinkBatch,
  stopListenerAgencyLinkBatch,
} from "../../actions/agencyAccountLinking";
import {
  AGENCY_ACCOUNT_LINKING_TOAST_DONE_ONE,
  AGENCY_ACCOUNT_LINKING_TOAST_DONE_OTHER,
  AGENCY_ACCOUNT_LINKING_TOAST_LATER,
  AGENCY_ACCOUNT_LINKING_TOAST_PARTIAL,
} from "../../i18n/keysTranslations";
import {
  AGENCY_LINKING_SOURCE,
  AGENCY_LINKING_TOAST_TONE,
  AGENCY_SHEET_LINK_OUTCOME,
  agencyLinkBatchToastView,
  applyLinkResultsToAccounts,
  buildAgencyLinkingSurface,
  buildAgencySheetLinkFeedback,
  collectIntegrationAdAccounts,
  shouldReplaceAgencyLinkingPrompt,
  summarizeLinkResults,
  toAgencyLinkingLocale,
} from "../../utils/agencyAccountLinking";
import {
  consumePendingAgencyLink,
  isAgencyLinkingOAuthCallbackUrl,
  requestResumePendingLink,
  setAgencyAccountLinkingEnabled,
  setAgencyLinkingAvailability,
  subscribeAgencyLinkingPicker,
  subscribeAgencyLinkingResume,
} from "../../agencyAccountLinking/session";
import useAgencyIsAppSumo from "../../hooks/useAgencyIsAppSumo";
import { reconnectForAgencyLink } from "../../agencyAccountLinking/reconnectForMissingScope";
import { AgencyAccountLinkingContext } from "../../agencyAccountLinking/agencyAccountLinkingContext";
import useAgencyAccountLinkingEnabled from "../../hooks/useAgencyAccountLinkingEnabled";
import AgencyAccountLinkingToast from "./AgencyAccountLinkingToast";
import ModalAgencyClientAccounts from "./ModalAgencyClientAccounts";
import {
  AGENCY_ACCOUNT_LINKING_PICKER_SHEET_DESCRIPTION,
  AGENCY_ACCOUNT_LINKING_PICKER_SHEET_TITLE,
} from "../../i18n/keysTranslations";

const summaryToast = ({ t, summary }) => {
  switch (summary.kind) {
    case "done":
      return {
        tone: AGENCY_LINKING_TOAST_TONE.SUCCESS,
        message:
          summary.count === 1
            ? t(AGENCY_ACCOUNT_LINKING_TOAST_DONE_ONE)
            : t(AGENCY_ACCOUNT_LINKING_TOAST_DONE_OTHER, {
                count: summary.count,
              }),
      };
    case "partial":
      return {
        tone: AGENCY_LINKING_TOAST_TONE.SUCCESS,
        message: t(AGENCY_ACCOUNT_LINKING_TOAST_PARTIAL, {
          linked: summary.linked,
          total: summary.total,
        }),
      };
    case "later":
      return {
        tone: AGENCY_LINKING_TOAST_TONE.NEUTRAL,
        message: t(AGENCY_ACCOUNT_LINKING_TOAST_LATER),
      };
    case "silent":
      return null;
    default: {
      const _unexpected = summary.kind;
      void _unexpected;
      return null;
    }
  }
};

const AgencyAccountLinkingProvider = ({ children }) => {
  const { t, i18n } = useTranslationApp();
  const dispatch = useDispatchApp();
  const enabled = useAgencyAccountLinkingEnabled();
  const { isAppSumo } = useAgencyIsAppSumo();
  const integrations = useSelectorApp((state) => state.user?.integrations);
  const [prompt, setPrompt] = useState(null);
  const [picker, setPicker] = useState(null);
  const [toast, setToast] = useState(null);
  const [linking, setLinking] = useState(false);
  const [completed, setCompleted] = useState(false);
  const [hidden, setHidden] = useState(false);
  const [revision, setRevision] = useState(0);
  const linkingRef = useRef(false);
  const pickerRef = useRef(null);
  const batchListenerRef = useRef(null);
  const toastedBatchIDsRef = useRef(new Set());

  const openPicker = useCallback((payload) => {
    pickerRef.current?.onClosed?.();
    pickerRef.current = payload;
    setPicker(payload);
  }, []);

  const closePicker = useCallback(() => {
    pickerRef.current?.onClosed?.();
    pickerRef.current = null;
    setPicker(null);
  }, []);

  const showToast = useCallback((nextToast) => {
    setToast(nextToast ? { ...nextToast, toastKey: Date.now() } : null);
  }, []);

  const hideToast = useCallback(() => setToast(null), []);

  const stopBatchListener = useCallback(() => {
    const listenerRef = batchListenerRef.current;
    batchListenerRef.current = null;
    if (!listenerRef) return;
    void dispatch(stopListenerAgencyLinkBatch({ listenerRef }));
  }, [dispatch]);

  const linkAccounts = useCallback(
    async ({
      accounts = [],
      source = AGENCY_LINKING_SOURCE.DASHBOARD_CARD,
      locale,
      resumeBatchID,
      retried = false,
      explainResult = false,
    } = {}) => {
      if (accounts.length === 0 || linkingRef.current) {
        return {
          ok: false,
          feedback: explainResult
            ? buildAgencySheetLinkFeedback({ response: { inProgress: true } })
            : null,
        };
      }

      linkingRef.current = true;
      setLinking(true);
      const resolvedLocale = locale || toAgencyLinkingLocale(i18n.language);
      let reauth = null;
      let batchID = null;

      const watchBatch = (nextBatchID) => {
        if (!nextBatchID) return;
        stopBatchListener();
        void dispatch(
          startListenerAgencyLinkBatch({
            batchID: nextBatchID,
            onReferenceAvailable: (listenerRef) => {
              batchListenerRef.current = listenerRef;
            },
            onUpdatedDataCallback: (batch) => {
              const view = agencyLinkBatchToastView(batch);
              if (!view) return;
              if (toastedBatchIDsRef.current.has(nextBatchID)) {
                stopBatchListener();
                return;
              }
              if (view.kind === "silent") {
                stopBatchListener();
                return;
              }
              toastedBatchIDsRef.current.add(nextBatchID);
              showToast(summaryToast({ t, summary: view }));
              if (
                view.kind === "done" &&
                source !== AGENCY_LINKING_SOURCE.CONNECTION_PICKER
              ) {
                setCompleted(true);
              }
              stopBatchListener();
            },
          })
        );
      };

      try {
        const response = await dispatch(
          startLinkAdAccountsToAgency({
            source,
            locale: resolvedLocale,
            accounts,
            resumeBatchID,
          })
        );

        if (response.consentBlocked) {
          setHidden(true);
          closePicker();
          return { ok: false };
        }

        const results = response.results || [];

        if (explainResult && results.length > 0) {
          setPrompt((current) => {
            if (!current?.accounts?.length) return current;
            return {
              ...current,
              accounts: applyLinkResultsToAccounts({
                accounts: current.accounts,
                results,
              }),
            };
          });
        }

        // Only the open agency sheet explains a result; other surfaces
        // stay quiet or use the summary toast.
        const feedback = explainResult
          ? buildAgencySheetLinkFeedback({
              response: { ...response, results },
              accounts,
            })
          : null;

        if (response.inProgress) {
          return {
            ok: false,
            feedback,
            results: explainResult ? results : undefined,
          };
        }

        // Platform list was stale or empty: refresh the prompt so the UI
        // reflects what the backend allows.
        if (response.skipped || response.notConfigured) {
          setRevision((current) => current + 1);
          return { ok: false, feedback };
        }

        // The OAuth confirm takes over and the resumed link toasts, so no
        // sheet feedback is returned.
        if (response.needsReauth?.[0] && response.batchID && !retried) {
          reauth = response.needsReauth[0];
          batchID = response.batchID;
          return { ok: false };
        }

        if (feedback?.outcome === AGENCY_SHEET_LINK_OUTCOME.ISSUE) {
          if (!explainResult) setRevision((current) => current + 1);
          return {
            ok: false,
            feedback,
            results: explainResult ? results : undefined,
          };
        }

        if (!response.ok && response.summary.total === 0) {
          if (response.batchID) {
            toastedBatchIDsRef.current.add(response.batchID);
          }
          showToast(summaryToast({ t, summary: { kind: "later" } }));
          watchBatch(response.batchID);
          return { ok: false };
        }

        const summary = summarizeLinkResults({ summary: response.summary });
        if (explainResult) {
          if (response.batchID) {
            toastedBatchIDsRef.current.add(response.batchID);
          }
        } else {
          const toast = summaryToast({ t, summary });
          if (toast && response.batchID) {
            toastedBatchIDsRef.current.add(response.batchID);
          }
          showToast(toast);
          if (
            summary.kind === "done" &&
            source !== AGENCY_LINKING_SOURCE.CONNECTION_PICKER
          ) {
            setCompleted(true);
          }
        }
        watchBatch(response.batchID);
        if (!explainResult) setRevision((current) => current + 1);
        return {
          ok: summary.kind === "done" || summary.kind === "partial",
          feedback,
          results: explainResult ? results : undefined,
        };
      } finally {
        linkingRef.current = false;
        setLinking(false);
        if (reauth) {
          void reconnectForAgencyLink({
            dispatch,
            t,
            platform: reauth.platform,
            accounts,
            source,
            locale: resolvedLocale,
            resumeBatchID: batchID,
          });
        }
      }
    },
    [closePicker, dispatch, i18n.language, showToast, stopBatchListener, t]
  );

  const resumePendingLink = useCallback(async () => {
    const pending = consumePendingAgencyLink();
    if (!pending?.accounts?.length) return;
    await linkAccounts({
      accounts: pending.accounts,
      source: pending.source,
      locale: pending.locale,
      resumeBatchID: pending.resumeBatchID,
      retried: true,
    });
  }, [linkAccounts]);

  useEffect(() => {
    setAgencyAccountLinkingEnabled(enabled);
  }, [enabled]);

  useEffect(() => {
    const unsubscribe = subscribeAgencyLinkingPicker(openPicker);
    return () => {
      unsubscribe();
      closePicker();
    };
  }, [closePicker, openPicker]);

  useEffect(
    () =>
      subscribeAgencyLinkingResume(() => {
        void resumePendingLink();
      }),
    [resumePendingLink]
  );

  useEffect(() => {
    if (!enabled || isAgencyLinkingOAuthCallbackUrl()) return;
    requestResumePendingLink();
  }, [enabled]);

  useEffect(() => () => stopBatchListener(), [stopBatchListener]);

  useEffect(() => {
    if (!enabled) {
      setPrompt(null);
      return undefined;
    }
    let cancelled = false;

    const load = async () => {
      const next = await dispatch(startGetAgencyLinkingPrompt());
      if (cancelled) return;
      setPrompt((current) =>
        shouldReplaceAgencyLinkingPrompt(current, next) ? next : current
      );
      if (next?.consentBlocked) setHidden(true);
    };

    void load();
    return () => {
      cancelled = true;
    };
  }, [dispatch, enabled, revision]);

  const refreshPrompt = useCallback(() => {
    setRevision((current) => current + 1);
  }, []);

  const integrationAccounts = useMemo(
    () =>
      collectIntegrationAdAccounts({
        integrations,
        availablePlatforms: prompt?.availablePlatforms || [],
      }),
    [integrations, prompt?.availablePlatforms]
  );

  const surfacePrompt = useMemo(
    () =>
      buildAgencyLinkingSurface({
        prompt,
        isAppSumo,
        integrationAccounts,
      }),
    [integrationAccounts, isAppSumo, prompt]
  );

  const surfacePromptRef = useRef(surfacePrompt);
  surfacePromptRef.current = surfacePrompt;
  const availabilityKey = [
    (surfacePrompt?.availablePlatforms || []).join("|"),
    surfacePrompt?.consentBlocked === true,
    surfacePrompt?.showPickerLine === true,
  ].join(":");

  useEffect(() => {
    const current = surfacePromptRef.current;
    if (!current) return;
    setAgencyLinkingAvailability({
      availablePlatforms: current.availablePlatforms || [],
      consentBlocked: current.consentBlocked === true,
      showPickerLine: current.showPickerLine === true,
      ready: true,
    });
  }, [availabilityKey]);

  const value = useMemo(
    () => ({
      linkAccounts,
      linking,
      prompt: surfacePrompt,
      completed,
      hidden,
      refreshPrompt,
    }),
    [completed, hidden, linkAccounts, linking, refreshPrompt, surfacePrompt]
  );

  return (
    <AgencyAccountLinkingContext.Provider value={value}>
      {children}
      {picker ? (
        <ModalAgencyClientAccounts
          key={picker.integrationID || "new-connection"}
          modalOpen
          accounts={picker.accounts}
          onClose={closePicker}
          onLinkAccounts={linkAccounts}
          titleKey={AGENCY_ACCOUNT_LINKING_PICKER_SHEET_TITLE}
          descriptionKey={AGENCY_ACCOUNT_LINKING_PICKER_SHEET_DESCRIPTION}
          linkingSource={AGENCY_LINKING_SOURCE.CONNECTION_PICKER}
        />
      ) : null}
      <AgencyAccountLinkingToast
        message={toast?.message || ""}
        tone={toast?.tone}
        toastKey={toast?.toastKey || 0}
        onDismiss={hideToast}
      />
    </AgencyAccountLinkingContext.Provider>
  );
};

export default AgencyAccountLinkingProvider;
